import { Box, Dialog as MuiDialog, IconButton, Typography, useMediaQuery, type Theme } from '@mui/material';
import { X } from 'lucide-react';
import { useId, type ReactElement, type ReactNode } from 'react';

import { Icon } from './Icon';

/**
 * Boite de dialogue (maquette : rayon 20, en-tete, corps, pied d'actions).
 *
 * Le `Dialog` de MUI apporte ce qu'une boite modale doit garantir et que le kit
 * ne reimplemente pas : **piege de focus** (Tab ne sort pas de la boite), focus
 * rendu a l'element d'origine a la fermeture, **Echap** pour fermer, voile qui
 * rend le reste de la page inerte. Le kit y ajoute la mise en page de la
 * maquette et le cablage `aria-labelledby` / `aria-describedby`, oublie une
 * fois sur deux quand chaque ecran ecrit le sien.
 *
 * Sous 900 px, la boite occupe tout l'ecran (comportement adaptatif de la coque,
 * maquette P20) : une boite centree de 560 px ne laisse a 390 px qu'une colonne
 * etroite entre deux marges.
 *
 * `onClose` absent : la boite ne se ferme ni par Echap, ni par le voile, ni par
 * la croix — c'est l'etat « operation en cours », ou fermer laisserait croire
 * qu'on a annule ce qui part deja au serveur.
 *
 * **Seul le corps defile.** L'en-tete (le titre nomme ce sur quoi on agit) et
 * le pied (le bouton qui agit) restent visibles quelle que soit la hauteur du
 * contenu : un `Paper` qui defilerait d'un bloc couperait le bouton d'une
 * action irreversible sur un portable de 900 px de haut, et ferait disparaitre
 * le titre pendant la saisie sur telephone. Le plein ecran sous 900 px (voir
 * plus haut) donne au corps toute la hauteur restante entre les deux.
 */
export interface DialogProps {
  readonly open: boolean;
  /** Absent pendant une operation : la boite ne peut alors pas etre fermee. */
  readonly onClose?: () => void;
  readonly title: string;
  /** Phrase d'introduction, annoncee a l'ouverture avec le titre. */
  readonly description?: ReactNode;
  readonly children?: ReactNode;
  /** Pied de la boite : boutons, le principal en dernier. */
  readonly actions?: ReactNode;
  readonly width?: 'sm' | 'md';
}

export const Dialog = ({
  open,
  onClose,
  title,
  description,
  children,
  actions,
  width = 'sm',
}: DialogProps): ReactElement => {
  const titleId = useId();
  const descriptionId = useId();
  const fullScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth={width}
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      aria-describedby={description === undefined ? undefined : descriptionId}
      // Le `Paper` ne defile plus : il est une colonne dont seul le corps defile.
      slotProps={{ paper: { sx: { overflow: 'hidden' } } }}
    >
      <Box
        data-dialog-region="en-tete"
        sx={{ flex: 'none', display: 'flex', alignItems: 'flex-start', gap: 2, px: 3, pt: 2.5, pb: 1.5 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={titleId} component="h2" sx={{ fontSize: 18, fontWeight: 800, lineHeight: '24px' }}>
            {title}
          </Typography>
          {description === undefined ? null : (
            <Typography id={descriptionId} component="div" sx={{ mt: 0.5, fontSize: 14, color: 'tokens.textMuted' }}>
              {description}
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Fermer" onClick={onClose} disabled={onClose === undefined} sx={{ mr: -1, mt: -0.5, color: 'tokens.textMuted' }}>
          <Icon icon={X} size={18} />
        </IconButton>
      </Box>
      {children === undefined ? null : (
        // `minHeight: 0` : sans lui, un enfant de flex ne retrecit pas sous la
        // hauteur de son contenu, et c'est la boite entiere qui deborde.
        //
        // Le champ qui recoit le focus — par Tab comme par un toucher — est
        // amene ENTIER dans la zone visible du corps : le navigateur ne le
        // fait qu'a moitie quand le champ touche le bord bas, juste au-dessus
        // du pied epingle. `nearest` ne bouge rien quand il est deja visible ;
        // `scrollPaddingBlock` garde une marge pour que l'aide sous le champ
        // ne colle pas au pied. `scrollIntoView` manque sous jsdom : garde.
        <Box
          data-dialog-region="corps"
          onFocus={(event) => {
            const target = event.target as HTMLElement;

            if (typeof target.scrollIntoView === 'function') {
              target.scrollIntoView({ block: 'nearest' });
            }
          }}
          sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', scrollPaddingBlock: '16px', px: 3, pb: 2 }}
        >
          {children}
        </Box>
      )}
      {actions === undefined ? null : (
        <Box
          data-dialog-region="pied"
          sx={{
            flex: 'none',
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1,
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'tokens.border',
            bgcolor: 'tokens.surfaceMuted',
          }}
        >
          {actions}
        </Box>
      )}
    </MuiDialog>
  );
};
